import {
   ChangeDetectionStrategy,
   Component,
   Input,
   OnChanges,
   SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { PortfolioRiskSeries } from '../../../domain/corporate/portfolio-risk-series';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
   selector: 'app-portfolio-risk-chart',
   standalone: true,
   imports: [CommonModule, ChartModule, SkeletonModule],
   templateUrl: './portfolio-risk-chart.component.html',
   styleUrls: ['./portfolio-risk-chart.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush // important
})
export class PortfolioRiskChartComponent implements OnChanges {
   @Input() series: PortfolioRiskSeries | null = null;
   @Input() loading = false;
   @Input() errorStatus: number | null = null;
   @Input() title: string | null = null;

   hasData = false;

   // stable reference until series changes
   chartData: any = {
      labels: [],
      datasets: []
   };

   get displayTitle(): string {
      return (this.title ?? '').trim() || 'Risk Over Selected Range';
   }

   ngOnChanges(changes: SimpleChanges): void {
      // only rebuild data when the input series changes
      if (changes['series']) {
         this.rebuildChartData();
      }
   }

   private rebuildChartData(): void {
      const labels = this.series?.labels ?? [];
      const fin = this.series?.financialRiskByDay ?? [];
      const nonFin = this.series?.nonFinancialTasksByDay ?? [];

      this.hasData = fin.some((x) => x > 0) || nonFin.some((x) => x > 0);

      // assign new object only when series changes (not on clicks)
      this.chartData = {
         labels,
         datasets: [
            {
               label: 'Monetary Risk',
               data: fin,
               borderColor: '#d81818',
               tension: 0.4,
               fill: false,
               pointRadius: 2,
               pointHoverRadius: 10,
               pointHitRadius: 10,
               borderWidth: 2,
               yAxisID: 'y'
            },
            {
               label: 'Non-Monetary Tasks',
               data: nonFin,
               borderColor: '#facc15',
               tension: 0.4,
               fill: false,
               pointRadius: 2,
               pointHoverRadius: 10,
               pointHitRadius: 10,
               borderWidth: 2,
               yAxisID: 'y1'
            }
         ]
      };
   }

   // keep options stable (don’t recreate in template/getters)
   readonly chartOptions: any = {
      responsive: true,

      // stop “re-animation” on redraw/update
      animation: { duration: 0 },
      transitions: {
         active: { animation: { duration: 0 } },
         resize: { animation: { duration: 0 } }
      },

      plugins: {
         legend: {
            position: 'bottom',
            labels: {
               color: '#1f2937',
               usePointStyle: true,
               boxWidth: 10
            }
         },
         tooltip: { mode: 'index', intersect: false },
         datalabels: { display: false }
      },
      scales: {
         x: { ticks: { display: false }, grid: { display: false } },
         y: {
            position: 'left',
            title: { display: true, text: 'Monetary Risk', color: '#d81818' },
            ticks: {
               color: '#d81818',
               callback: (value: number) =>
                  value >= 1000 ? `$${value.toFixed(2)}` : `${value}`
            },
            grid: { drawOnChartArea: false }
         },
         y1: {
            position: 'right',
            title: { display: true, text: 'Non-Monetary Tasks', color: '#facc15' },
            ticks: { color: '#facc15', stepSize: 1 },
            grid: { color: '#e5e7eb' }
         }
      }
   };
}
